import { z } from 'zod';

/**
 * A module manifest is a module's self-description: what it depends on, what it
 * publishes, what it consumes and what it charges for. The registry validates
 * the dependency graph from these before anything is activated.
 */
export const moduleManifestSchema = z.object({
  key: z.string().regex(/^[A-Z][A-Z0-9_]*$/, 'Module keys are SCREAMING_SNAKE_CASE'),
  name: z.string().min(1),
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Semantic version required'),
  descriptionKey: z.string(),
  category: z.enum([
    'PLATFORM',
    'FOUNDATION',
    'ENGAGEMENT',
    'MINISTRY',
    'FINANCE',
    'OPERATIONS',
    'DIGITAL',
    'INTELLIGENCE',
    'ENTERPRISE',
  ]),
  type: z.enum(['PLATFORM', 'APPLICATION', 'INTEGRATION']),
  /** Hard dependencies. Must be active before this module can activate. */
  dependencies: z.array(z.string()).default([]),
  /** Enhances behaviour when present, but never blocks activation. */
  optionalDependencies: z.array(z.string()).default([]),
  permissions: z.array(z.string()).default([]),
  features: z.array(z.string()).default([]),
  eventsPublished: z.array(z.string()).default([]),
  eventsConsumed: z.array(z.string()).default([]),
  routes: z.array(z.string()).default([]),
  settings: z.array(z.string()).default([]),
  supportedLocales: z.array(z.string()).default([]),
  /** Modules the tenant does not choose; they are always on. */
  isCore: z.boolean().default(false),
  status: z.enum(['ACTIVE', 'BETA', 'DEPRECATED']).default('ACTIVE'),
});

export type ModuleManifest = z.infer<typeof moduleManifestSchema>;

export const MODULE_INSTALLATION_STATUSES = [
  'NOT_INSTALLED',
  'INSTALLING',
  'ACTIVE',
  'DISABLED',
  'FAILED',
] as const;
export type ModuleInstallationStatus = (typeof MODULE_INSTALLATION_STATUSES)[number];

export interface ModuleInstallationResponse {
  readonly moduleKey: string;
  readonly name: string;
  readonly version: string;
  readonly status: ModuleInstallationStatus;
  readonly isCore: boolean;
  /** False when the plan does not include this module, even while installed. */
  readonly isEntitled: boolean;
  readonly activatedAt: string | null;
  readonly dependencies: readonly string[];
  /** Active modules that depend on this one; blocks disabling. */
  readonly dependents: readonly string[];
}

// --- Platform module catalog administration ----------------------------------

/**
 * One module's full catalog entry: the technical facts code already
 * declares (key, category, dependencies, lifecycle status — none of it
 * editable here) alongside the commercial facts a platform operator sets
 * (title, description, price). `configured` is false until an operator has
 * saved a row for this module at least once; until then `title`/
 * `description` fall back to the code-defined name, `isFree` defaults to
 * true, and `priceMonthlyCents` is null.
 */
export interface ModuleCatalogEntryResponse {
  readonly key: string;
  readonly category: ModuleManifest['category'];
  readonly type: ModuleManifest['type'];
  readonly status: ModuleManifest['status'];
  readonly isCore: boolean;
  /** Other modules this one cannot function without. */
  readonly dependencies: readonly string[];
  readonly title: string;
  readonly description: string;
  readonly isFree: boolean;
  readonly priceMonthlyCents: number | null;
  readonly configured: boolean;
  readonly updatedAt: string | null;
  readonly updatedByUserId: string | null;
}

/**
 * The pre-auth surface of the module catalog — just enough for a
 * registration wizard (or a returning owner managing their bundle) to show
 * the whole system, not just the shop. Excludes fields that are only
 * meaningful to a platform operator (`configured`, `updatedAt`,
 * `updatedByUserId`). Every module the code declares is listed, including
 * the always-on baseline (every `PLATFORM` module, plus `PEOPLE`) — a
 * prospective church should see the complete picture before deciding what
 * to add, not just the add-ons. `isBaseline` is what the client uses to
 * render those as "included" rather than a toggle: never sent back as part
 * of a chosen bundle, since they're never a purchase decision.
 */
export interface PublicModuleCatalogEntryResponse {
  readonly key: string;
  readonly category: ModuleManifest['category'];
  readonly dependencies: readonly string[];
  readonly title: string;
  readonly description: string;
  readonly isFree: boolean;
  readonly priceMonthlyCents: number | null;
  readonly isBaseline: boolean;
}

export const updateModuleCommercialInfoRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(150),
    description: z.string().trim().max(1000),
    isFree: z.boolean(),
    priceMonthlyCents: z.coerce.number().int().min(0).max(1_000_000_00).nullish(),
  })
  .refine((value) => value.isFree || (value.priceMonthlyCents ?? null) !== null, {
    message: 'A monthly price is required for a paid module',
    path: ['priceMonthlyCents'],
  });
export type UpdateModuleCommercialInfoRequest = z.infer<
  typeof updateModuleCommercialInfoRequestSchema
>;
